import React from 'react';
import { motion } from 'framer-motion';
import { FaFacebook, FaInstagram, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { SCHEDULE_PATH, TRANSPORT_PATH, juniorCupPath } from '../config/externalLinks';
import './Footer.css';

const Footer: React.FC = () => {
  const { t, language } = useLanguage();
  return (
    <footer id="contact" className="footer">
      <div className="footer__main">
        <div className="container">
          <div className="footer__content">
            <motion.div
              className="footer__section footer__about"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
                    <div className="footer__logo">
                      <img
                        src="/images/footer-logo.png"
                        alt="FK Junior Novi Sad Logo"
                        onError={(e) => {
                          // Fallback to JPG if PNG doesn't exist
                          const target = e.target as HTMLImageElement;
                          target.src = "/images/footer-logo.jpg";
                        }}
                      />
                    </div>
              <p className="footer__description">
                {t('footer.description')}
              </p>
              <div className="footer__social">
                <a href="https://www.facebook.com/JUNIORFKCOM" target="_blank" rel="noopener noreferrer" className="footer__social-link">
                  <FaFacebook />
                </a>
                <a href="https://www.instagram.com/fkjuniornovisad/" target="_blank" rel="noopener noreferrer" className="footer__social-link">
                  <FaInstagram />
                </a>
              </div>
            </motion.div>

            <motion.div
              className="footer__section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="footer__section-title">{t('footer.quickLinks')}</h3>
              <ul className="footer__links">
                <li><a href="#home">{t('footer.navigation.home')}</a></li>
                <li><a href="/vesti/">{t('footer.links.news')}</a></li>
                <li><a href="#skola-fudbala">{t('header.school')}</a></li>
                <li><a href="#training-experience">{t('footer.navigation.trainingExperience')}</a></li>
                <li><a href="#achievements">{t('footer.navigation.achievements')}</a></li>
                <li><a href="#team-spirit">{t('footer.navigation.aboutUs')}</a></li>
                <li><a href="#contact">{t('footer.navigation.contact')}</a></li>
              </ul>
            </motion.div>

            <motion.div
              className="footer__section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="footer__section-title">{t('footer.programs')}</h3>
              <ul className="footer__links">
                <li><a href="#skola-fudbala">{t('footer.links.youthAcademy')}</a></li>
                <li><a href="/fudbal-za-decu-novi-sad/">{t('footer.links.kids')}</a></li>
                <li><a href="/fudbalski-klub-novi-sad/">{t('footer.links.club')}</a></li>
                <li><a href="#training-experience">{t('footer.links.trainingCamps')}</a></li>
                <li><a href="#achievements">{t('footer.links.competitiveTeams')}</a></li>
                <li><a href={juniorCupPath(language)}>{t('header.juniorCup2027')}</a></li>
                <li><a href="/upis/">{t('footer.links.privateCoaching')}</a></li>
                <li><a href={SCHEDULE_PATH}>{t('footer.links.schedule')}</a></li>
                <li><a href={TRANSPORT_PATH}>{t('footer.links.transport')}</a></li>
              </ul>
            </motion.div>

            <motion.div
              className="footer__section footer__contact-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="footer__section-title">{t('footer.contactInfo')}</h3>
              <div className="footer__contact">
                <a href="https://maps.app.goo.gl/kxhuAwESArRMpWxw9" target="_blank" rel="noopener noreferrer" className="footer__contact-item footer__contact-link">
                  <div className="footer__contact-icon-wrapper">
                    <FaMapMarkerAlt className="footer__contact-icon" />
                  </div>
                  <div className="footer__contact-text">
                    <span className="footer__contact-label">{t('footer.contact.locationLabel')}</span>
                    <span className="footer__contact-value">{t('footer.contact.location')}</span>
                  </div>
                </a>
                <a href="tel:+381631134892" className="footer__contact-item footer__contact-link">
                  <div className="footer__contact-icon-wrapper">
                    <FaPhone className="footer__contact-icon" />
                  </div>
                  <div className="footer__contact-text">
                    <span className="footer__contact-label">{t('footer.contact.phoneLabel')}</span>
                    <span className="footer__contact-value">{t('footer.contact.phone')}</span>
                  </div>
                </a>
                <a href="mailto:info@juniorfk.com" className="footer__contact-item footer__contact-link">
                  <div className="footer__contact-icon-wrapper">
                    <FaEnvelope className="footer__contact-icon" />
                  </div>
                  <div className="footer__contact-text">
                    <span className="footer__contact-label">{t('footer.contact.emailLabel')}</span>
                    <span className="footer__contact-value">{t('footer.contact.email')}</span>
                  </div>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="footer__bottom">
        <div className="container">
          <div className="footer__bottom-content">
            <motion.p
              className="footer__copyright"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              {t('footer.copyright').replace('{year}', String(new Date().getFullYear()))}
            </motion.p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
